import React, { Fragment, useEffect, useState } from 'react';
import { Download, Play } from 'lucide-react';
import { Menu, Transition } from "@headlessui/react";
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { Checkbox } from 'primereact/checkbox';


// Match your actual data structure from analytics.tsx
interface Playlist {
  playlist: {
    uuid: string;
    name: string;
    imageUrl: string;
    platform: string;
    owner?: { name: string };
    type?: string;
  };
  peakPosition: number;
  position: number;
  entryDate: string;
  exitDate?: string;
  positionDate?: string;
  peakPositionDate?: string;
  isEditorial: boolean;
  playlistType: "editorial" | "algorithmic" | "algotorial";
}

// CSV Export data structure (only needed fields for export)
interface CSVExportData {
  playlist: string;
  platform: string;
  type: string;
  peakPosition: number;
  currentPosition: number;
  exitDate: string;
  entryDate: string;
}

interface CSVDownloadProps {
  activePlatform: string
}


interface MusicPlatform {
  name: string;
  code: string;
}


const CSVDownload: React.FC<CSVDownloadProps> = (activePlatform) => {

  const [platforms, setPlatforms] = useState<MusicPlatform[]>([]);
  const [data, setData] = useState<Playlist[]>([]);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [exportPlatforms, setExportPlatforms] = useState<string[]>([]);

  const [selectedPlatforms, setSelectedPlatforms] = useState<MusicPlatform[]>([]); // Default to Spotify

  const onPlatformChange = (e: any) => {
    let _selectedPlatforms = [...selectedPlatforms];

    if (e.checked) {
      _selectedPlatforms.push(e.value);
    } else {
      _selectedPlatforms = _selectedPlatforms.filter(platform => platform.code !== e.value.code);
    }

    setSelectedPlatforms(_selectedPlatforms);
  };

  const musicPlatforms: MusicPlatform[] = [
    { name: 'Spotify', code: 'spotify' },
    { name: 'Apple Music', code: 'apple' },
    { name: 'Amazon Music', code: 'amazon' },
    { name: 'Deezer', code: 'deezer' },
    { name: 'SoundCloud', code: 'soundcloud' },
    { name: 'TIDAL', code: 'tidal' },
    { name: 'Audiomack', code: 'audiomack' }
  ];


  // useEffect(() => {
  //   const stored = localStorage.getItem('playlists');
  //   if (stored) {
  //     const parsed = JSON.parse(stored);
  //     Object.keys(parsed).forEach((key) => {
  //       setPlatforms(prev => [...prev, { code: key, name: key }]);

  //     });
  //   }
  // }, []);
  useEffect(() => {
    const stored = localStorage.getItem('playlists');

    if (stored) {
      const parsed = JSON.parse(stored);
      const extractedPlatforms = Object.keys(parsed).map(key => ({
        code: key,
        name: key
      }));
      setPlatforms(extractedPlatforms);
      const playListArray = Object.values(parsed).flat();
      setData(playListArray as Playlist[]);
    }
  }, [activePlatform]);



  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const transformToCSVData = (items: Playlist[]): CSVExportData[] => {
    return items.filter((item) => selectedPlatforms.some(platform => platform.code === item.playlist.platform)).map(item => ({
      playlist: item.playlist.name,
      platform: item.playlist.platform,
      type: item.playlistType,
      peakPosition: item.peakPosition,
      currentPosition: item.position,
      exitDate: item.exitDate ? formatDate(item.exitDate) : '',
      entryDate: formatDate(item.entryDate)
    }));
  };  

  const downloadCSV = async (): Promise<void> => {
    setIsExporting(true);

    try {
      const csvData = transformToCSVData(data);

      const headers: string[] = [
        'Playlist',
        'Platform',
        'Type',
        'Peak_Position',
        'Current_Position',
        'Exit_Date',
        'Entry_Date'
      ];

      // const csvContent = [
      //   headers.join(','),
      //   ...csvData.map((item: CSVExportData) => [
      //     `"${item.playlist}"`,
      //     item.platform,
      //     item.type,
      //     item.peakPosition.toString(),
      //     item.currentPosition.toString(),
      //     item.exitDate,
      //     item.entryDate
      //   ].join(','))
      // ].join('\n');

      const delimiter = ';'; // 👈 Use semicolon for Excel compatibility

      const csvContent = [
        headers.join(delimiter),
        ...csvData.map((item: CSVExportData) => [
          `"${item.playlist}"`,
          item.platform,
          item.type,
          item.peakPosition.toString(),
          item.currentPosition.toString(),
          item.exitDate,
          item.entryDate
        ].join(delimiter))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `playlists-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CSV download failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <Menu as="div" className="relative inline-block text-left">
        <Menu.Button className="p-2 rounded-md hover:bg-gray-200">
          <EllipsisVerticalIcon className="w-5 h-5" />
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items as='div' className="absolute right-0 z-10 p-4 bg-white divide-y divide-gray-100 shadow-lg min-w-96 rounded-xl top-10 ">
            <div className='grid grid-cols-2 gap-4'>
              {platforms.map((platform) => {
                return (
                  <div key={platform.code} className="flex items-center">
                    <Checkbox
                      inputId={platform.code}
                      name="platform"
                      value={platform}
                      onChange={onPlatformChange}
                      checked={selectedPlatforms.some((item) => item.code === platform.code)}
                    />
                    <label htmlFor={platform.code} className="ml-2">
                      {platform.name}
                    </label>
                  </div>
                );
              })}
            </div>
            <div className='flex justify-end mt-4 '>
              <button
                onClick={downloadCSV}
                disabled={selectedPlatforms.length === 0 || data.length === 0 || isExporting}
                className="flex items-center gap-2 px-4 py-2 mt-4 text-sm font-medium text-white transition-all duration-200 ease-in-out bg-teal-500 rounded hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="flex-shrink-0 w-4 h-4" />
                {isExporting ? 'Exporting...' : `Export CSV`}
              </button>

            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
};

export default CSVDownload;